import { useAppDispatch, useAppSelector } from '@/app'
import { LoginStateHandler } from '@/features/auth'
import { SignInFlowWrapper } from '@/features/auth/SignInFlow'
import { ConfigMainView, ResourceStreamSynchronizer } from '@/features/core'
import {
  OpenFeatureClientProvider,
  useFlag,
  useOpenFeatureClient,
} from '@/features/feature-flags'
import { PropertiesProvider } from '@/features/feature-flags/PropertiesProvider'
import { PersonalPlanMainView } from '@/features/personal-plan'
import { PlanningMainView } from '@/features/planning'
import {
  RouteBrowserMainView,
  selectors as routeSelectors,
} from '@/features/route-browser'
import { SplashScreen } from '@/features/splash-screen'
import { assertUnreachable } from '@alexandrainst/assert-unreachable'
import { useClient } from '@alexandrainst/plana-react-api'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import SettingsIcon from '@mui/icons-material/Settings'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import ViewDayIcon from '@mui/icons-material/ViewDay'
import {
  Alert,
  AlertTitle,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  Container,
  Divider,
} from '@mui/material'
import {
  useIsRestoring,
  useQueryErrorResetBoundary,
} from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { selectors, actions as shellActions } from '../shellSlice'
import { ErrorBoundary } from './ErrorBoundary'
import { useStyles } from './MobileMainShellView.style'

const Content = () => {
  const focus = useAppSelector(selectors.selectFocus)
  switch (focus) {
    case 'planning':
      return <PlanningMainView />
    case 'routeBrowser':
      return <RouteBrowserMainView />
    case 'singleRoute':
      return <PersonalPlanMainView />
    case 'config':
      return <ConfigMainView />
    case 'splash':
      return <SplashScreen />
    case 'customers':
      return <></>
    default:
      assertUnreachable(focus)
  }
}

/* TODO: This is currently 1:1 the same as DesktopMainShellView */
export const MobileMainShellView = () => {
  const isRestoring = useIsRestoring()
  const { classes } = useStyles()
  const { t } = useTranslation()
  const { reset } = useQueryErrorResetBoundary()
  const focus = useAppSelector(selectors.selectFocus)
  const {
    state: { isRouteSelctionOpen },
  } = useAppSelector(routeSelectors.selectRouteBrowser)
  const dispatch = useAppDispatch()
  const planaClient = useClient()
  const globalFeatureClient = useOpenFeatureClient()
  const flagProvider = useMemo(
    () => new PropertiesProvider(planaClient, globalFeatureClient),
    [globalFeatureClient, planaClient]
  )
  const singleRouteMode = useFlag('singleRouteMode')
  const showAllRoutes = useFlag('showAllRoutes')

  useEffect(() => {
    if (singleRouteMode && focus === 'planning') {
      dispatch(shellActions.focusSelected('singleRoute'))
    }
  }, [dispatch, singleRouteMode, focus])

  if (isRestoring) {
    return <SplashScreen />
  }

  return (
    <OpenFeatureClientProvider provider={flagProvider}>
      <SignInFlowWrapper>
        <ErrorBoundary
          beforeCapture={scope => {
            scope.setTag('source', 'MobileMainShellView')
          }}
          onReset={reset}
          fallback={({ resetError, error }) => (
            <Container
              component="main"
              maxWidth="xs"
              className={classes.container}
            >
              <Alert
                severity="error"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => resetError()}
                  >
                    <Trans t={t} i18nKey="shell.buttons.tryAgain">
                      Try again
                    </Trans>
                  </Button>
                }
              >
                <AlertTitle>
                  <Trans t={t} i18nKey="common.error">
                    Error
                  </Trans>
                </AlertTitle>
                <Trans t={t} i18nKey="shell.messages.unexpectedError">
                  There was an unexpected error
                </Trans>
                <Divider sx={{ margin: '5px' }} />
                <Box>
                  [
                  {error instanceof Error
                    ? `[${error.message}]`
                    : '[Unknown error]'}
                  ]
                </Box>
              </Alert>

              <LoginStateHandler />
            </Container>
          )}
        >
          <Content />
          <LoginStateHandler />
          <ResourceStreamSynchronizer />
        </ErrorBoundary>

        <BottomNavigation
          showLabels
          value={focus}
          onChange={(_: unknown, newFocus) => {
            dispatch(shellActions.focusSelected(newFocus))
          }}
        >
          {!singleRouteMode && (
            <BottomNavigationAction
              disabled={singleRouteMode}
              value="planning"
              label={t('shell.bottomNav.planning')}
              icon={<CalendarMonthIcon />}
            />
          )}
          {showAllRoutes && (
            <BottomNavigationAction
              value="routeBrowser"
              label={
                focus === 'routeBrowser' && !isRouteSelctionOpen
                  ? t('shell.bottomNav.routeBrowserEdit')
                  : t('shell.bottomNav.routeBrowser')
              }
              icon={<ViewColumnIcon />}
            />
          )}

          <BottomNavigationAction
            value="singleRoute"
            label={t('shell.bottomNav.myRoute')}
            icon={<ViewDayIcon />}
          />
          <BottomNavigationAction
            value="config"
            label={t('shell.bottomNav.config')}
            icon={<SettingsIcon />}
          />
        </BottomNavigation>
      </SignInFlowWrapper>
    </OpenFeatureClientProvider>
  )
}
